import { useMemo } from 'react';
import { partition } from 'lodash';
import { useQuery } from 'react-query';
import { Gql } from '@fiverr-private/dynamic_matching';
import { getContext } from '@fiverr-private/fiverr_context';
import { FieldOptionsResponse, RegionFieldValue } from '../../../../../../api';
import { useFieldOptions } from '../../../../../../hooks';
import { getCountryName } from '../../../utils/utils';
import { ListOption } from '../../../utils/types';

export const useLocation = () => {
  const { getRegionsAndTimelineOptions } = useFieldOptions();
  const { countryCode } = getContext();

  const { data: regionData } = useQuery(['DM_FIELD_OPTIONS_REGION'], () =>
    getRegionsAndTimelineOptions<FieldOptionsResponse<RegionFieldValue>>(['region'], 'REGION')
  );

  const regionOptions = useMemo(() => {
    const regionOptions = regionData?.data?.fields?.find((field) => field.type === 'region');
    return (regionOptions?.values || [])
      .map((region) => ({
        value: region.key,
        label: region.meta.display_name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [regionData]);

  const countriesOptions = useMemo(() => {
    const countries = (Object.values(Gql.DmBriefCountryCode) as string[])
      .map((country) => {
        const label = getCountryName(country);
        return label !== country ? { label, value: country } : undefined;
      })
      .filter((country): country is ListOption => country !== undefined)
      .sort((a, b) => a.label.localeCompare(b.label));

    const [userCountry, restOptions] = partition(countries, (country) => country.value === countryCode);
    return [...userCountry, ...restOptions];
  }, [countryCode]);

  return {
    regionOptions,
    countriesOptions,
  };
};
